import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { ServiceProof } from "@/components/site/ServiceProof";
import { ServiceFaqSection } from "@/components/site/ServiceFaq";
import { faqsForService } from "@/lib/service-faqs";
import { caseStudiesForService } from "@/lib/service-proof";
import type { ServiceDetail } from "@/lib/service-details";
import { serviceDetails } from "@/lib/service-details";

const SITE = "https://finendra.com";

export function serviceDetailHead(service: ServiceDetail) {
  const url = `${SITE}/services/${service.slug}`;
  const image = `${SITE}/og/og-services.png`;
  return {
    meta: [
      { title: service.metaTitle },
      { name: "description", content: service.metaDescription },
      { property: "og:title", content: service.metaTitle },
      { property: "og:description", content: service.metaDescription },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: `${service.name} services by Finendra` },
      { property: "og:image:dark", content: `${SITE}/og/dark/og-services.png` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: image },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.name,
          serviceType: service.name,
          description: service.metaDescription,
          url,
          areaServed: "Worldwide",
          provider: { "@type": "Organization", name: "Finendra", url: `${SITE}/` },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${service.name} deliverables`,
            itemListElement: service.deliverables.flatMap((group) =>
              group.items.map((item) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Service", name: item },
              })),
            ),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
            { "@type": "ListItem", position: 3, name: service.name, item: url },
          ],
        }),
      },
      ...caseStudyJsonLd(service, url),
      ...faqJsonLd(service, url),
    ],
  };
}

/** FAQPage schema for the service-specific FAQ section. */
function faqJsonLd(service: ServiceDetail, url: string) {
  const faqs = faqsForService(service.slug);
  if (faqs.length === 0) return [];

  return [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        name: `${service.name} FAQ`,
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    },
  ];
}

/** ItemList of CaseStudy entries (with published metrics) for this service. */
function caseStudyJsonLd(service: ServiceDetail, url: string) {
  const studies = caseStudiesForService(service.slug);
  if (studies.length === 0) return [];

  return [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${service.name} case studies`,
        itemListElement: studies.map((s, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: {
            "@type": ["CaseStudy", "Article"],
            "@id": `${url}#case-${s.id}`,
            headline: s.headline,
            name: s.headline,
            url: `${url}#case-${s.id}`,
            abstract: s.challenge,
            articleBody: `${s.challenge} ${s.approach}`,
            about: { "@type": "Thing", name: s.industry },
            industry: s.industry,
            isPartOf: { "@type": "WebPage", "@id": url },
            author: { "@type": "Organization", name: "Finendra", url: `${SITE}/` },
            publisher: { "@type": "Organization", name: "Finendra", url: `${SITE}/` },
            provider: { "@type": "Organization", name: "Finendra", url: `${SITE}/` },
            mentions: { "@type": "Service", name: service.name, url },
            subjectOf: { "@type": "Organization", description: s.client },
            additionalProperty: s.metrics.map((m) => ({
              "@type": "PropertyValue",
              name: m.label,
              value: m.value,
            })),
          },
        })),
      }),
    },
  ];
}

/** Detail routes are static files; the slug maps 1:1 to `/services/<slug>`. */
const detailPath = (slug: string) => `/services/${slug}` as "/services/bookkeeping";

export function ServiceDetailPage({ service }: { service: ServiceDetail }) {
  const others = serviceDetails.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <>
      <PageHero eyebrow={service.eyebrow} title={service.title} body={service.intro} />

      <section className="px-6 pb-4">
        <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-brand-navy/50">
            <li>
              <Link to="/" className="hover:text-brand-accent">
                Home
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li>
              <Link to="/services" className="hover:text-brand-accent">
                Services
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li className="font-medium text-brand-navy/80">{service.name}</li>
          </ol>
        </nav>
      </section>

      {/* Outcomes */}
      <section className="px-6 py-10">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
          {service.outcomes.map((o, i) => (
            <Reveal key={o} delay={i * 0.07}>
              <div className="h-full rounded-3xl border border-brand-navy/5 bg-brand-surface p-8">
                <span className="font-display text-sm font-bold text-brand-accent">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-4 text-lg font-semibold leading-snug text-brand-navy">{o}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Deliverables */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              What you get
            </h2>
            <p className="mt-4 max-w-2xl text-brand-navy/60">
              Scope is written down before we start, so there is never a question about who owns
              what.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {service.deliverables.map((group, i) => (
              <Reveal key={group.group} delay={i * 0.07}>
                <div className="h-full rounded-3xl border border-brand-navy/5 bg-background p-8">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                    {group.group}
                  </h3>
                  <ul className="mt-6 space-y-3">
                    {group.items.map((item) => (
                      <li
                        key={item}
                        className="flex gap-3 text-sm leading-relaxed text-brand-navy/75"
                      >
                        <span
                          aria-hidden
                          className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-accent"
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Delivery timeline
            </h2>
            <p className="mt-4 max-w-2xl text-brand-navy/60">
              The operating cadence once your account is live.
            </p>
          </Reveal>
          <ol className="mt-10 border-l border-brand-navy/10 pl-6 md:pl-10">
            {service.timeline.map((t, i) => (
              <Reveal key={t.label} delay={i * 0.06}>
                <li className="relative pb-10 last:pb-0">
                  <span
                    aria-hidden
                    className="absolute -left-[31px] top-1.5 size-3 rounded-full border-2 border-background bg-brand-accent md:-left-[47px]"
                  />
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                    {t.when}
                  </p>
                  <h3 className="mt-2 text-xl font-bold text-brand-navy">{t.label}</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-brand-navy/60">
                    {t.detail}
                  </p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Onboarding */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-7xl rounded-[40px] bg-brand-surface p-10 md:p-16">
          <Reveal>
            <h2 className="font-display text-3xl font-bold text-brand-navy md:text-4xl">
              Onboarding schedule
            </h2>
            <p className="mt-4 max-w-2xl text-brand-navy/60">
              From kickoff to steady state, with realistic durations.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {service.onboarding.map((o, i) => (
              <Reveal key={o.step} delay={i * 0.07}>
                <div className="h-full rounded-3xl border border-brand-navy/5 bg-background p-7">
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
                    {o.duration}
                  </p>
                  <h3 className="mt-3 text-lg font-bold text-brand-navy">{o.step}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-navy/60">{o.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-brand-navy">Tools we work in</h2>
            <ul className="mt-6 flex flex-wrap gap-3">
              {service.tools.map((t) => (
                <li
                  key={t}
                  className="rounded-full border border-brand-navy/10 px-5 py-2 text-sm font-medium text-brand-navy/70"
                >
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <ServiceProof slug={service.slug} serviceName={service.name} />

      <ServiceFaqSection slug={service.slug} serviceName={service.name} />

      {/* Related services */}
      <section className="px-6 pb-8">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-brand-navy">Related services</h2>
          </Reveal>
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {others.map((o, i) => (
              <Reveal key={o.slug} delay={i * 0.05}>
                <Link
                  to={detailPath(o.slug)}
                  className="block h-full rounded-2xl border border-brand-navy/10 p-6 transition-[border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-1 hover:border-brand-accent/40 hover:shadow-lg active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none"
                >
                  <p className="font-bold text-brand-navy">{o.name}</p>
                  <p className="mt-2 text-sm text-brand-navy/55">{o.intro.slice(0, 78)}…</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaPanel
        title={`Ready to hand off ${service.name.toLowerCase()}?`}
        body="Tell us your volumes and current setup — we'll come back with a scoped plan and a start date."
        ctaLabel={`Get a ${service.name} proposal`}
        service={service.name}
      />
    </>
  );
}
